import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getUserActionedArticleOrNews, saveArticleOrNews } from "services";

import { formatDate, goTo, truncateString, useCancelAsyncOnUnmount, useGetFullArticle, useInView, useNavigate, useTranslation, useUserContext } from "utils";

import { Bookmark, Card, LazyLoadImage, Link, Skeleton, TagRecommended, TagText } from "components";

interface ArticleCardProps {
  link: string;
  moduleCalcCard?: boolean;
  recommendByDefault?: boolean;
  showLabel?: boolean;
}

export const ArticleCard = ({ link, moduleCalcCard, recommendByDefault, showLabel }: ArticleCardProps) => {
  const { user } = useUserContext();
  const { t } = useTranslation("articles");
  const { article } = useGetFullArticle({ link: link || "unkown" });

  const [userActions, setUserActions] = useState({
    allowed: false,
    saved: false,
    set: false,
  });
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    trackVisibility: true,
    delay: 100,
  });

  const loadUserActionedArticleOrNews = useCallback(() => {
    getUserActionedArticleOrNews({
      link,
      signal,
    }).then((res) => {
      setUserActions({
        allowed: !!res?.allowed,
        saved: !!res?.saved,
        set: true,
      });
    });
  }, [link, signal]);

  const toggleSave = () => {
    saveArticleOrNews({
      link: link,
      signal,
    }).then(loadUserActionedArticleOrNews);
  };

  const getTag = () => {
    if (moduleCalcCard) {
      return t("articleCard.measures");
    }
    if (article?.tags.length) {
      return article.tags[0].tag;
    }
    return null;
  };

  const getSecondTag = () => {
    if (article?.tags.length && article.tags[1]) {
      return article.tags[1].tag;
    }
    return null;
  };

  useEffect(() => {
    if (link && inView && user) {
      loadUserActionedArticleOrNews();
    }
  }, [link, inView, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref}>
      {article?.title ? (
        <Card
          className="article-card"
          onClick={(e) => goTo(e, navigate, user ? `/article/${article.link}` : `/article/${article.link}`)}
          mixpanelText={article.title}
        >
          <div className="image">
            <LazyLoadImage alt={article.altImageText} src={article.images.s} />
          </div>
          <div className="content">
            <div className="tags">
              {(article.recommended || recommendByDefault) && <TagRecommended text={t("articleCard.recommended")} />}
              {showLabel && getSecondTag() ? <TagRecommended text={getSecondTag()} /> : <></>}
              <TagText text={getTag()} />
              {userActions.allowed && user && (
                <span onClick={(e) => e.stopPropagation()}>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Bookmark colour="#202020" checked={userActions.saved} onChange={toggleSave} initialValue={userActions.saved} />
                </span>
              )}
            </div>
            <div className="headline">
              <Link to={user ? `/article/${article.link}` : `/article/${article.link}`} onClick={(e) => e.stopPropagation()} mixpanelText={article.title}>
                <h3>{truncateString(article.title, 72)}</h3>
              </Link>
            </div>
            <div className="date-and-reading-time">
              <div className="date">
                {article.updatedAt
                  ? formatDate(article.updatedAt, "DD.MM.YYYY", moment.ISO_8601)
                  : formatDate(article.createdAt, "DD.MM.YYYY", moment.ISO_8601)}
              </div>
              <div className="reading-time margin-left-50">{t("articleCard.readingTime", { time: article.readingTime })}</div>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="article-card">
          <Skeleton type="block" height="192px" />
          <div className="content">
            <Skeleton type="title" />
            <Skeleton type="paragraph" rows={3} />
          </div>
        </Card>
      )}
    </div>
  );
};

export default ArticleCard;
